<template>
  <v-card>
    <v-card-title class="headline d-flex justify-space-between align-center py-2">
      Gestionar apadrinamiento
      <v-btn icon :disabled="saving" @click="$emit('close-dialog')"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>
    <v-divider />
    <v-card-text class="pt-4 pb-0">
      <div class="primary--text mb-2" style="font-weight: bold; font-size: 1.2rem">{{ sponsorship.name || '-' }}</div>
      <v-sheet class="grey lighten-4 rounded pa-3">
        <v-row dense>
          <v-col cols="7" class="py-1">
            <div class="text-caption grey--text">Organización</div>
            <div class="font-weight-medium">{{ sponsorship.organization || '-' }}</div>
          </v-col>
          <v-col cols="5" class="py-1">
            <div class="text-caption grey--text">Plan</div>
            <div class="font-weight-medium">{{ sponsorship.plansName || '-' }}</div>
          </v-col>
          <v-col cols="12" class="py-1">
            <div class="text-caption grey--text">Fecha de inicio</div>
            <div class="font-weight-medium">{{ sponsorship.startDate || '-' }}</div>
          </v-col>
        </v-row>
      </v-sheet>
    </v-card-text>

    <div class="px-6 pt-4">
      <div class="text-subtitle-1 font-weight-bold mb-1">Estado del apadrinamiento</div>
    </div>
    <v-card-text class="py-2">
      <v-radio-group v-model="selectedStatus" row class="mt-0" data-test="status-radio">
        <v-radio
          v-for="option in statusOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
          :color="statusColor(option.value)"
          class="mx-4"
        />
      </v-radio-group>
      <v-textarea
        v-if="deactivating"
        v-model="deactivationReason"
        label="Motivo de desactivación *"
        placeholder="Escribe el motivo de desactivación..."
        outlined
        rows="3"
        :counter="REASON_MAX_LENGTH"
        :maxlength="REASON_MAX_LENGTH"
        data-test="deactivation-reason"
      />
      <v-alert v-if="statusChanged" type="info" dense text class="mb-0">
        Solo cambia el estado: el cobro en MercadoPago / Stripe no se modifica y no se envía correo.
        Si la mascota fue adoptada, usa «Cambiar mascota».
      </v-alert>
      <v-alert v-if="error" type="error" dense text class="mt-3 mb-0">{{ error }}</v-alert>
    </v-card-text>

    <v-expansion-panels flat>
      <v-expansion-panel elevation="0">
        <v-expansion-panel-header>
          <span class="text-subtitle-2" style="font-weight: 600;">Historial de estados</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div v-if="historyLoading" class="d-flex justify-center py-4">
            <v-progress-circular indeterminate color="primary" size="36" />
          </div>
          <div v-else-if="historyError" class="text-center red--text caption py-2">{{ historyError }}</div>
          <div v-else-if="!history.length" class="text-center grey--text caption py-2" data-test="history-empty">
            Sin historial de cambios de estado.
          </div>
          <v-timeline v-else dense data-test="history">
            <v-timeline-item v-for="item in history" :key="item.id" :color="statusColor(item.newStatus)" small>
              <div class="font-weight-medium">{{ statusLabel(item.newStatus) }}</div>
              <div class="text-caption">{{ formatDate(item.createdAt) }}</div>
              <div v-if="item.deactivationReason" class="text-caption">Motivo: «{{ item.deactivationReason }}»</div>
            </v-timeline-item>
          </v-timeline>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-divider />
    <v-card-actions class="d-flex justify-end">
      <v-btn text color="grey darken-1" :disabled="saving" @click="$emit('close-dialog')">Cerrar</v-btn>
      <v-btn color="primary" :loading="saving" :disabled="!canSave" data-test="save-status" @click="save">
        Guardar cambios
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import SponsorshipService from '@/services/sponsorship.service';
import DateUtils from '@/utils/date.utils.js';

const ACTIVE = 2;
const DEACTIVATED = 4;
const REASON_MAX_LENGTH = 300;

// El admin solo activa o desactiva; otros estados (p.ej. pendiente de pago) solo se muestran.
const STATUS_OPTIONS = [
  { label: 'Activo', value: ACTIVE },
  { label: 'Desactivado', value: DEACTIVATED }
];

const STATUS_COLORS = { [ACTIVE]: 'green', [DEACTIVATED]: 'red' };

// Activar / desactivar un apadrinamiento desde la 360, con historial de cambios (como MembershipManager).
// Se monta dentro de un v-dialog; emite `changed` al guardar y `close-dialog` al cerrar.
export default {
  name: 'SponsorshipManager',
  props: {
    // Apadrinamiento de la 360: { id, name, organization, plansName, startDate, status }.
    sponsorship: { type: Object, required: true }
  },
  emits: ['changed', 'close-dialog', 'submitting'],
  data() {
    return {
      REASON_MAX_LENGTH,
      statusOptions: STATUS_OPTIONS,
      selectedStatus: parseInt(this.sponsorship.status, 10),
      deactivationReason: '',
      saving: false,
      error: '',
      history: [],
      historyLoading: false,
      historyError: ''
    };
  },
  computed: {
    statusChanged() {
      return this.selectedStatus !== parseInt(this.sponsorship.status, 10);
    },
    deactivating() {
      return this.statusChanged && this.selectedStatus === DEACTIVATED;
    },
    canSave() {
      return this.statusChanged && (!this.deactivating || this.deactivationReason.trim().length > 0);
    }
  },
  created() {
    this.loadHistory();
  },
  methods: {
    async loadHistory() {
      this.historyLoading = true;
      this.historyError = '';
      const res = await SponsorshipService.getStatusHistory(this.sponsorship.id);
      this.historyLoading = false;
      if (res.status !== 'ok') {
        this.historyError = res.message;
        return;
      }
      this.history = res.data;
    },
    async save() {
      this.error = '';
      this.saving = true;
      this.$emit('submitting', true);
      const reason = this.deactivating ? this.deactivationReason.trim() : null;
      const res = await SponsorshipService.changeStatus(this.sponsorship.id, this.selectedStatus, reason);
      this.saving = false;
      this.$emit('submitting', false);
      if (res.status !== 'ok') {
        this.error = res.message;
        return;
      }
      this.$emit('changed', this.selectedStatus);
    },
    statusLabel(status) {
      const option = STATUS_OPTIONS.find(o => o.value === status);
      return option ? option.label : `Estado ${status}`;
    },
    statusColor(status) {
      return STATUS_COLORS[status] || 'primary';
    },
    formatDate(dateStr) {
      const d = new Date(dateStr);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${DateUtils.formatToChileanDate(d)} - ${hh}:${mm}`;
    }
  }
};
</script>
